type ErrorLike = {
    name?: string
    message?: string
    statusCode?: number
    status?: number
    response?: { status?: number }
    data?: { statusCode?: number }
}

function toMessage(error: unknown): string {
    const e = error as ErrorLike
    return String(e?.message || error || '')
}

export function getStatusCode(error: unknown): number | null {
    const e = error as ErrorLike
    return e?.statusCode ?? e?.status ?? e?.response?.status ?? e?.data?.statusCode ?? null
}

export function isTimeoutRequest(error: unknown): boolean {
    const msg = toMessage(error).toLowerCase()

    return msg.includes('timeout')
        || msg.includes('timed out')
        || msg.includes('etimedout')
}

export function isAbortedRequest(error: unknown): boolean {
    if (isTimeoutRequest(error)) {
        return false
    }

    const e = error as ErrorLike
    const msg = toMessage(error).toLowerCase()
    const name = String(e?.name || '').toLowerCase()

    return name === 'aborterror'
        || name.includes('abort')
        || msg.includes('aborted')
        || msg.includes('cancelled')
        || msg.includes('canceled')
        || (msg.includes('abort') && !msg.includes('timeout'))
}

export function isOfflineClient(): boolean {
    return import.meta.client && typeof navigator !== 'undefined' && navigator.onLine === false
}

export function isNetworkNoResponse(error: unknown): boolean {
    const e = error as ErrorLike
    const msg = toMessage(error).toLowerCase()
    const name = String(e?.name || '').toLowerCase()
    const statusCode = getStatusCode(error)

    if (statusCode) return false

    return name.includes('axioserror')
        || name.includes('fetcherror')
        || msg.includes('network error')
        || msg.includes('failed to fetch')
        || msg.includes('load failed')
        || msg.includes('network request failed')
}

export function classifyNetworkError(error: unknown): {
    statusCode: number | null
    isAborted: boolean
    isOffline: boolean
    isTimeout: boolean
    isNetworkNoResponse: boolean
    isHttpError: boolean
} {
    const statusCode = getStatusCode(error)
    const isTimeout = isTimeoutRequest(error)
    const isAborted = isAbortedRequest(error)
    const isOffline = isOfflineClient()
    const isNoResponse = isNetworkNoResponse(error) || isTimeout

    return {
        statusCode,
        isAborted,
        isOffline,
        isTimeout,
        isNetworkNoResponse: isNoResponse,
        isHttpError: Boolean(statusCode && statusCode >= 400),
    }
}

